import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent 
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import CadastralCacheManagement from "@/components/CadastralCacheManagement";
import OverpassApiManagement from "@/components/OverpassApiManagement";
import SearchApiManagement from "@/components/SearchApiManagement";
import { serviceIntegrationsApi } from "@/services/api-v2.service";
import { CadastralDbConfig, ServiceIntegration, ServiceIntegrationCreate, ServiceIntegrationUpdate } from "@/types/api-v2";
import {
  Map as MapIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckCircle2 as CheckCircleIcon,
  XCircle as XCircleIcon,
  Search,
  Building2
} from "lucide-react";

type ServiceIntegrationFormData = {
  name?: string;
  description?: string;
  serviceType: string;
  serviceName: string;
  isActive: boolean;
  settings: Record<string, any>;
};

// Предопределенные шаблоны настроек для разных типов сервисов
const SERVICE_TEMPLATES = {
  map: {
    openstreetmap: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
      subdomains: ["a", "b", "c"]
    },
    custom: {
      url: "",
      attribution: "",
      maxZoom: 18,
      subdomains: []
    }
  },
  search: {
    nominatim: {
      url: "https://nominatim.openstreetmap.org",
      params: {
        format: "json",
        limit: 1,
        "accept-language": "ru"
      }
    },
    nominatim_local: {
      url: "http://localhost:8080",
      params: {
        format: "json",
        limit: 1,
        "accept-language": "ru"
      }
    },
    custom: {
      url: "",
      params: {
        format: "json",
        limit: 1,
        "accept-language": "ru"
      }
    }
  },
  cadastral: {
    rosreestr: {
      nspd_base: "https://nspd.gov.ru",
      search_api: "https://nspd.gov.ru/api/geoportal/v1/search/geoportal",
      intersects_api: "https://nspd.gov.ru/api/geoportal/v1/intersects",
      wms_base_template: "https://nspd.gov.ru/api/aeggis/v3/{layer_id}/wms",
      parcel_layer_id: "36368",
      thematic_search_id: "1",
      nspd_ssl_verify: false,
      primary_api: "https://nspd.gov.ru/api/geoportal/v1/search/geoportal",
      detailed_api: "https://nspd.gov.ru/api/geoportal/v1/search/geoportal",
      timeout: 10000,
      retry_attempts: 3,
      description:
        "НСПД (nspd.gov.ru): search по КН + spatial/WMS. parcel_layer_id=36368 — участки ЕГРН; 36945 — только округа, не участки.",
    },
    cache: {
      use_cache: true,
      cache_ttl_hours: 24,
      auto_update: true,
      data_sources: ["openstreetmap", "manual"],
      admin_levels: [4, 5, 6],
      max_cache_size_mb: 100,
      cleanup_old_data: true,
      description: "Настройки кэширования кадастровых данных"
    }
  }
};

export default function ServiceIntegrationManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Состояния для диалогов и форм
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<ServiceIntegration | null>(null);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(null);

  // Форма для создания/редактирования интеграции
  const [integrationForm, setIntegrationForm] = useState<ServiceIntegrationFormData>({
    serviceType: "cadastral",
    serviceName: "rosreestr",
    isActive: true,
    settings: { ...SERVICE_TEMPLATES.cadastral.rosreestr }
  });
  const [cadastralDbForm, setCadastralDbForm] = useState<CadastralDbConfig>({
    host: "",
    port: 5432,
    database: "",
    username: "",
    password: "",
    sslMode: "",
    isEnabled: true,
    sourceMode: "hybrid",
  });

  // Запрос данных для интеграций
  const { data: integrations = [], isLoading: isLoadingIntegrations } = useQuery<ServiceIntegration[]>({
    queryKey: ['service-integrations'],
    queryFn: () => serviceIntegrationsApi.list(),
  });
  const { data: cadastralDbConfig, isLoading: isLoadingCadastreDbConfig } = useQuery<CadastralDbConfig>({
    queryKey: ['service-integrations', 'cadastral', 'db-config'],
    queryFn: () => serviceIntegrationsApi.getCadastralDbConfig(),
  });

  useEffect(() => {
    if (cadastralDbConfig) {
      setCadastralDbForm(cadastralDbConfig);
    }
  }, [cadastralDbConfig]);

  const saveCadastralDbConfigMutation = useMutation({
    mutationFn: async () => serviceIntegrationsApi.updateCadastralDbConfig(cadastralDbForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-integrations', 'cadastral', 'db-config'] });
      toast({
        title: "Настройки сохранены",
        description: "Подключение к внешней БД cadastre обновлено",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка сохранения",
        description: error?.message || "Не удалось сохранить настройки подключения к БД",
        variant: "destructive",
      });
    },
  });

  const testCadastralDbConfigMutation = useMutation({
    mutationFn: async () => serviceIntegrationsApi.testCadastralDbConfig(cadastralDbForm),
    onSuccess: (result) => {
      toast({
        title: result.status === "success" ? "Подключение успешно" : "Ошибка подключения",
        description: result.message,
        variant: result.status === "success" ? "default" : "destructive",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка теста",
        description: error?.message || "Не удалось выполнить тест подключения",
        variant: "destructive",
      });
    },
  });
  
  // Получение интеграций по типу
  const mapIntegrations = integrations.filter(integration => 
    integration.serviceType === 'map'
  );
  
  const searchIntegrations = integrations.filter(integration => 
    integration.serviceType === 'search'
  );
  
  const cadastralIntegrations = integrations.filter(integration => 
    integration.serviceType === 'cadastral'
  );
  
  // Открытие диалога создания новой интеграции
  const openIntegrationDialog = (integration?: ServiceIntegration) => {
    if (integration) {
      setIsEditMode(true);
      setSelectedIntegration(integration);
      setIntegrationForm({
        name: integration.name || undefined,
        description: integration.description || undefined,
        serviceType: integration.serviceType,
        serviceName: integration.serviceName,
        isActive: integration.isActive,
        settings: (integration.settings as Record<string, any>) || {}
      });
    } else {
      setIsEditMode(false);
      setSelectedIntegration(null);
      setIntegrationForm({
        name: undefined,
        description: undefined,
        serviceType: "map",
        serviceName: "openstreetmap",
        isActive: true,
        settings: { ...SERVICE_TEMPLATES.map.openstreetmap }
      });
    }
    setIsIntegrationDialogOpen(true);
  };
  
  // Подтверждение удаления интеграции
  const confirmDeleteIntegration = (id: number) => {
    setSelectedIntegrationId(id);
    setIsConfirmDeleteOpen(true);
  };
  
  // Обработка удаления интеграции
  const handleDeleteIntegration = async () => {
    if (!selectedIntegrationId) return;
    
    try {
      await serviceIntegrationsApi.delete(selectedIntegrationId);
      
      // Обновление списка интеграций
      queryClient.invalidateQueries({ queryKey: ['service-integrations'] });
      
      setIsConfirmDeleteOpen(false);
      
      toast({
        title: "Интеграция удалена",
        description: "Интеграция успешно удалена из системы"
      });
    } catch (error) {
      console.error("Error deleting integration:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось удалить интеграцию",
        variant: "destructive"
      });
    }
  };
  
  // Обработка изменения активности интеграции
  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      const updateData: ServiceIntegrationUpdate = { isActive };
      await serviceIntegrationsApi.update(id, updateData);
      
      // Обновление списка интеграций
      queryClient.invalidateQueries({ queryKey: ['service-integrations'] });
      
      toast({
        title: isActive ? "Интеграция активирована" : "Интеграция деактивирована",
        description: isActive 
          ? "Интеграция успешно активирована" 
          : "Интеграция успешно деактивирована"
      });
    } catch (error) {
      console.error("Error toggling integration status:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось изменить статус интеграции",
        variant: "destructive"
      });
    }
  };
  
  // Обработка изменения типа сервиса в форме
  const handleServiceTypeChange = (value: string) => {
    const newForm = { ...integrationForm, serviceType: value };
    
    // Обновляем шаблон настроек в зависимости от типа сервиса
    if (value === 'map') {
      newForm.serviceName = 'openstreetmap';
      newForm.settings = { ...SERVICE_TEMPLATES.map.openstreetmap };
    } else if (value === 'search') {
      newForm.serviceName = 'nominatim';
      newForm.settings = { ...SERVICE_TEMPLATES.search.nominatim };
    } else if (value === 'cadastral') {
      newForm.serviceName = 'rosreestr';
      newForm.settings = { ...SERVICE_TEMPLATES.cadastral.rosreestr };
    }
    
    setIntegrationForm(newForm);
  };
  
  // Обработка изменения названия сервиса в форме
  const handleServiceNameChange = (value: string) => {
    const newForm = { ...integrationForm, serviceName: value };
    
    // Обновляем шаблон настроек в зависимости от названия сервиса
    if (integrationForm.serviceType === 'map' && SERVICE_TEMPLATES.map[value as keyof typeof SERVICE_TEMPLATES.map]) {
      newForm.settings = { ...SERVICE_TEMPLATES.map[value as keyof typeof SERVICE_TEMPLATES.map] };
    } else if (integrationForm.serviceType === 'search' && SERVICE_TEMPLATES.search[value as keyof typeof SERVICE_TEMPLATES.search]) {
      newForm.settings = { ...SERVICE_TEMPLATES.search[value as keyof typeof SERVICE_TEMPLATES.search] };
    } else if (integrationForm.serviceType === 'cadastral' && SERVICE_TEMPLATES.cadastral[value as keyof typeof SERVICE_TEMPLATES.cadastral]) {
      newForm.settings = { ...SERVICE_TEMPLATES.cadastral[value as keyof typeof SERVICE_TEMPLATES.cadastral] };
    }
    
    setIntegrationForm(newForm);
  };
  
  // Обработка изменения настроек JSON
  const handleSettingsChange = (value: string) => {
    try {
      const settings = JSON.parse(value);
      setIntegrationForm(prev => ({ ...prev, settings }));
    } catch (error) {
      // Если JSON невалидный, просто не обновляем настройки
      console.error("Invalid JSON:", error);
    }
  };
  
  // Сохранение интеграции
  const handleSaveIntegration = async () => {
    try {
      if (isEditMode && selectedIntegration) {
        // Обновление существующей интеграции
        const updateData: ServiceIntegrationUpdate = {
          name: integrationForm.name || undefined,
          description: integrationForm.description || undefined,
          serviceType: integrationForm.serviceType,
          serviceName: integrationForm.serviceName,
          isActive: integrationForm.isActive,
          settings: integrationForm.settings,
        };
        
        await serviceIntegrationsApi.update(selectedIntegration.id, updateData);
        
        toast({
          title: "Интеграция обновлена",
          description: `Интеграция "${integrationForm.name || integrationForm.serviceName}" успешно обновлена`
        });
      } else {
        // Создание новой интеграции
        const createData: ServiceIntegrationCreate = {
          name: integrationForm.name || "",
          description: integrationForm.description || "",
          serviceType: integrationForm.serviceType,
          serviceName: integrationForm.serviceName,
          isActive: integrationForm.isActive,
          settings: integrationForm.settings,
        };
        
        await serviceIntegrationsApi.create(createData);
        
        toast({
          title: "Интеграция создана",
          description: `Интеграция "${integrationForm.name || integrationForm.serviceName}" успешно создана`
        });
      }
      
      // Обновление списка интеграций
      queryClient.invalidateQueries({ queryKey: ['service-integrations'] });
      
      setIsIntegrationDialogOpen(false);
    } catch (error) {
      console.error("Error saving integration:", error);
      toast({
        title: "Ошибка",
        description: isEditMode 
          ? "Не удалось обновить интеграцию" 
          : "Не удалось создать интеграцию",
        variant: "destructive"
      });
    }
  };
  
  return (
    <div className="p-4">
      <Tabs defaultValue="cadastral" className="w-full">
        <div className="flex justify-between items-center mb-4">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 gap-2 h-auto p-1">
            <TabsTrigger value="maps" className="flex items-center gap-2 text-sm py-2.5">
              <MapIcon className="h-4 w-4" /> Карты
            </TabsTrigger>
            <TabsTrigger value="search" className="flex items-center gap-2 text-sm py-2.5">
              <Search className="h-4 w-4" /> Поиск на карте
            </TabsTrigger>
            <TabsTrigger value="cadastral" className="flex items-center gap-2 text-sm py-2.5">
              <Building2 className="h-4 w-4" /> Кадастры
            </TabsTrigger>
          </TabsList>
          <Button onClick={() => openIntegrationDialog()} className="ml-4 flex-shrink-0">
            <PlusIcon className="h-4 w-4 mr-1" /> Добавить интеграцию
          </Button>
        </div>
        
        <TabsContent value="maps" className="space-y-4 mt-4">
          <h4 className="text-lg font-medium">Интеграции с картографическими сервисами</h4>
          {isLoadingIntegrations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Управление Overpass API */}
              <OverpassApiManagement />
              
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/6">Название</TableHead>
                  <TableHead className="w-1/6">Тип</TableHead>
                  <TableHead className="w-3/6">Настройки</TableHead>
                  <TableHead className="w-1/12">Статус</TableHead>
                  <TableHead className="w-1/12">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mapIntegrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">Нет данных</TableCell>
                  </TableRow>
                ) : (
                  mapIntegrations.map(integration => (
                    <TableRow key={integration.id}>
                      <TableCell className="font-medium">{integration.name || integration.serviceName}</TableCell>
                      <TableCell>{integration.serviceType}</TableCell>
                      <TableCell className="font-mono text-xs">
                        <pre className="max-h-24 overflow-auto rounded-md border border-border bg-muted/50 p-2 text-xs text-foreground whitespace-pre-wrap break-all">
                          {JSON.stringify(integration.settings, null, 2)}
                        </pre>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className={integration.isActive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
                          onClick={() => handleToggleActive(integration.id, !integration.isActive)}
                        >
                          {integration.isActive ? (
                            <CheckCircleIcon className="h-5 w-5" />
                          ) : (
                            <XCircleIcon className="h-5 w-5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openIntegrationDialog(integration)}
                            title="Редактировать"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-600 dark:text-red-400" 
                            onClick={() => confirmDeleteIntegration(integration.id)}
                            title="Удалить"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </>
          )}
        </TabsContent>
        
        <TabsContent value="search" className="space-y-4 mt-4">
          <h4 className="text-lg font-medium">Интеграции с сервисами поиска на карте</h4>
          {isLoadingIntegrations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Управление сервисом поиска */}
              <SearchApiManagement />
              
              <div className="rounded-lg border border-border bg-muted/40 p-4 mb-6">
                <h4 className="font-medium text-foreground mb-2">Информация о сервисах поиска</h4>
                <p className="text-sm text-muted-foreground">
                  Для работы в закрытом контуре без доступа к интернету настройте локальный Nominatim сервер.
                  Укажите URL локального сервера и параметры запроса для поиска мест на карте.
                </p>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/6">Название</TableHead>
                    <TableHead className="w-1/6">Тип сервиса</TableHead>
                    <TableHead className="w-3/6">Настройки</TableHead>
                    <TableHead className="w-1/12">Статус</TableHead>
                    <TableHead className="w-1/12">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchIntegrations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                        Нет настроенных интеграций с сервисами поиска на карте
                      </TableCell>
                    </TableRow>
                  ) : (
                    searchIntegrations.map(integration => (
                      <TableRow key={integration.id}>
                        <TableCell className="font-medium">{integration.name || integration.serviceName}</TableCell>
                        <TableCell>
                          {integration.serviceName === 'nominatim' ? 'OpenStreetMap Nominatim' : 
                           integration.serviceName === 'nominatim_local' ? 'Локальный Nominatim' : 
                           'Пользовательский'}
                        </TableCell>
                        <TableCell>
                          <pre className="max-h-24 overflow-auto rounded-md border border-border bg-muted/50 p-2 text-xs font-mono text-foreground whitespace-pre-wrap break-all">
                            {JSON.stringify(integration.settings, null, 2)}
                          </pre>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className={integration.isActive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
                            onClick={() => handleToggleActive(integration.id, !integration.isActive)}
                          >
                            {integration.isActive ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <XCircleIcon className="h-5 w-5" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openIntegrationDialog(integration)}
                              title="Редактировать"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-600 dark:text-red-400" 
                              onClick={() => confirmDeleteIntegration(integration.id)}
                              title="Удалить"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </TabsContent>
        
        <TabsContent value="cadastral" className="space-y-4 mt-4">
          <h4 className="text-lg font-medium">Интеграции с кадастровыми сервисами</h4>
          {isLoadingIntegrations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-border bg-muted/40 p-4 mb-4">
                <h4 className="font-medium text-foreground mb-2">О кадастровых интеграциях</h4>
                <p className="text-sm text-muted-foreground">
                  Настройте интеграцию с российскими кадастровыми сервисами для получения детальной информации о земельных участках, зданиях и сооружениях. 
                  Интеграция позволяет получать сведения о кадастровой стоимости, правообладателях, характеристиках объектов недвижимости.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-background p-4 space-y-4">
                <h4 className="font-medium text-foreground">Подключение к внешней БД cadastre</h4>
                {isLoadingCadastreDbConfig ? (
                  <div className="flex items-center justify-center py-3">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-host">Host</Label>
                        <Input
                          id="cadastre-db-host"
                          value={cadastralDbForm.host}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, host: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-port">Port</Label>
                        <Input
                          id="cadastre-db-port"
                          type="number"
                          value={cadastralDbForm.port}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, port: Number(e.target.value) || 5432 }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-name">Database</Label>
                        <Input
                          id="cadastre-db-name"
                          value={cadastralDbForm.database}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, database: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-user">Username</Label>
                        <Input
                          id="cadastre-db-user"
                          value={cadastralDbForm.username}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, username: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-password">Password</Label>
                        <Input
                          id="cadastre-db-password"
                          type="password"
                          value={cadastralDbForm.password}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, password: e.target.value }))}
                          autoComplete="new-password"
                        />
                        <p className="text-xs text-muted-foreground">
                          После сохранения пароль в ответе API скрыт (***). Оставьте *** или пусто, чтобы не менять
                          сохранённый пароль.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-ssl">SSL mode</Label>
                        <Input
                          id="cadastre-db-ssl"
                          value={cadastralDbForm.sslMode || ""}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, sslMode: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-source-mode">Режим источника</Label>
                        <select
                          id="cadastre-source-mode"
                          className="w-full border border-input bg-background px-3 py-2 rounded-md text-foreground"
                          value={cadastralDbForm.sourceMode}
                          onChange={(e) => setCadastralDbForm((prev) => ({ ...prev, sourceMode: e.target.value }))}
                        >
                          <option value="legacy">legacy (только публичный API)</option>
                          <option value="external">external (архив; при недоступности — API в fetch)</option>
                          <option value="hybrid">hybrid (архив → API)</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Публичный источник — <strong>НСПД</strong> (<code>search_api</code> на
                          nspd.gov.ru). Режим <strong>hybrid</strong>/<strong>legacy</strong>: поиск
                          по КН и по геометрии объекта. Слой WMS <strong>36945</strong> — кадастровые
                          округа (не участки); для участков в settings —{" "}
                          <code>parcel_layer_id=36368</code>. Устаревший data2.php не использовать.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="cadastre-db-enabled">Интеграция активна</Label>
                        <div className="h-10 flex items-center">
                          <Switch
                            id="cadastre-db-enabled"
                            checked={cadastralDbForm.isEnabled}
                            onCheckedChange={(checked) => setCadastralDbForm((prev) => ({ ...prev, isEnabled: checked }))}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => testCadastralDbConfigMutation.mutate()}
                        disabled={testCadastralDbConfigMutation.isPending}
                      >
                        {testCadastralDbConfigMutation.isPending ? "Проверка..." : "Проверить подключение"}
                      </Button>
                      <Button
                        onClick={() => saveCadastralDbConfigMutation.mutate()}
                        disabled={saveCadastralDbConfigMutation.isPending}
                      >
                        {saveCadastralDbConfigMutation.isPending ? "Сохранение..." : "Сохранить"}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Управление кэшем кадастровых данных */}
              <CadastralCacheManagement />
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/6">Название</TableHead>
                    <TableHead className="w-1/6">Провайдер</TableHead>
                    <TableHead className="w-3/6">Настройки</TableHead>
                    <TableHead className="w-1/12">Статус</TableHead>
                    <TableHead className="w-1/12">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cadastralIntegrations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">
                        Нет настроенных интеграций с кадастровыми сервисами
                      </TableCell>
                    </TableRow>
                  ) : (
                    cadastralIntegrations.map(integration => (
                      <TableRow key={integration.id}>
                        <TableCell className="font-medium">{integration.name || integration.serviceName}</TableCell>
                        <TableCell>{integration.serviceName === 'rosreestr' ? 'Росреестр' : integration.serviceName}</TableCell>
                        <TableCell className="font-mono text-xs">
                          <pre className="max-h-24 overflow-auto rounded-md border border-border bg-muted/50 p-2 text-xs text-foreground whitespace-pre-wrap break-all">
                            {JSON.stringify(integration.settings, null, 2)}
                          </pre>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className={integration.isActive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}
                            onClick={() => handleToggleActive(integration.id, !integration.isActive)}
                          >
                            {integration.isActive ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <XCircleIcon className="h-5 w-5" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openIntegrationDialog(integration)}
                              title="Редактировать"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-600 dark:text-red-400" 
                              onClick={() => confirmDeleteIntegration(integration.id)}
                              title="Удалить"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Диалог создания/редактирования интеграции */}
      <Dialog open={isIntegrationDialogOpen} onOpenChange={setIsIntegrationDialogOpen}>
        <DialogContent className="max-w-md z-[1000]">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Редактирование интеграции" : "Создание новой интеграции"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="integration-name">Название интеграции</Label>
              <Input
                id="integration-name"
                placeholder="Например: Локальные тайлы"
                value={integrationForm.name || ''}
                onChange={(e) => setIntegrationForm(prev => ({ ...prev, name: e.target.value || undefined }))}
              />
              <p className="text-xs text-muted-foreground">
                Отображаемое название интеграции (необязательно)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="integration-description">Описание</Label>
              <Textarea
                id="integration-description"
                placeholder="Описание интеграции..."
                value={integrationForm.description || ''}
                onChange={(e) => setIntegrationForm(prev => ({ ...prev, description: e.target.value || undefined }))}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="service-type">Тип сервиса</Label>
              <select 
                id="service-type"
                className="w-full border border-input bg-background px-3 py-2 rounded-md text-foreground"
                value={integrationForm.serviceType}
                onChange={(e) => handleServiceTypeChange(e.target.value)}
              >
                <option value="map">Картографический сервис</option>
                <option value="search">Поиск на карте</option>
                <option value="cadastral">Кадастровые сервисы</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service-name">Название сервиса</Label>
              <select 
                id="service-name"
                className="w-full border border-input bg-background px-3 py-2 rounded-md text-foreground"
                value={integrationForm.serviceName}
                onChange={(e) => handleServiceNameChange(e.target.value)}
              >
                {integrationForm.serviceType === 'map' ? (
                  <>
                    <option value="openstreetmap">OpenStreetMap</option>
                    <option value="custom">Пользовательский</option>
                  </>
                ) : integrationForm.serviceType === 'search' ? (
                  <>
                    <option value="nominatim">OpenStreetMap Nominatim</option>
                    <option value="nominatim_local">Локальный Nominatim</option>
                    <option value="custom">Пользовательский</option>
                  </>
                ) : integrationForm.serviceType === 'cadastral' ? (
                  <>
                    <option value="rosreestr">Росреестр</option>
                  </>
                ) : (
                  <option value={integrationForm.serviceName}>{integrationForm.serviceName}</option>
                )}
              </select>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="is-active">Активен</Label>
                <Switch 
                  id="is-active" 
                  checked={integrationForm.isActive}
                  onCheckedChange={(checked) => setIntegrationForm(prev => ({ ...prev, isActive: checked }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="settings">Настройки (JSON)</Label>
              <Textarea 
                id="settings" 
                className="font-mono text-xs h-40 bg-background text-foreground border-border"
                value={JSON.stringify(integrationForm.settings, null, 2)}
                onChange={(e) => handleSettingsChange(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Настройки в формате JSON для конфигурации сервиса
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIntegrationDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSaveIntegration}>
              {isEditMode ? "Сохранить" : "Создать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Диалог подтверждения удаления интеграции */}
      <ConfirmDialog
        isOpen={isConfirmDeleteOpen}
        title="Удаление интеграции"
        description="Вы уверены, что хотите удалить эту интеграцию? Это действие нельзя отменить."
        onConfirm={handleDeleteIntegration}
        onCancel={() => setIsConfirmDeleteOpen(false)}
      />
    </div>
  );
}